import React, { useEffect, useState, useRef } from 'react';
import '../../component-styles/CompanySettings.css';
import API_BASE from '../../utils/api.js';
import { validateWebImageFile, resizeImageForLogo } from '../../utils/imageUpload.js';
import notify from '../../utils/notify';
import AddressAutocomplete from '../common/AddressAutocomplete';

export default function CompanySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);

  const userId = localStorage.getItem('userId');
  const apiUrl = API_BASE;

  const [form, setForm] = useState({
    companyName: '',
    address: '',
    contactNumber: '',
    email: '',
    website: '',
    ein: '',
    logoUrl: '',
  });

  /* ----------------------------------------
     Load settings on mount
  ---------------------------------------- */
  useEffect(() => {
    async function loadSettings() {
      try {
        const res = await fetch(`${apiUrl}/api/settings/${userId}`);
        const data = await res.json();

        setForm({
          companyName: data.company?.companyName || '',
          address: data.company?.address || '',
          contactNumber: data.company?.contactNumber || '',
          email: data.company?.email || '',
          website: data.company?.website || '',
          ein: data.company?.ein || '',
          logoUrl: data.company?.logoUrl || '',
        });
      } catch (err) {
        console.error('Failed to load settings', err);
      } finally {
        setLoading(false);
      }
    }

    loadSettings();
  }, [apiUrl, userId]);

  /* ----------------------------------------
     State update helper
  ---------------------------------------- */
  const updateField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  /* ----------------------------------------
     Save company info
  ---------------------------------------- */
  async function saveCompany() {
    setSaving(true);

    try {
      await fetch(`${apiUrl}/api/settings/${userId}/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      notify('Company info saved!');
    } catch (err) {
      console.error('Save error:', err);
      notify('Failed to save company settings.');
    }

    setSaving(false);
  }

  /* ----------------------------------------
     Upload with progress (XMLHttpRequest)
  ---------------------------------------- */
  async function uploadFile(file) {
    if (!file) return;

    const validation = validateWebImageFile(file);
    if (!validation.ok) {
      notify(validation.error);
      return;
    }

    setUploading(true);

    try {
      const resized = await resizeImageForLogo(file);
      const token = localStorage.getItem('idToken');
      const presignUrl = `${apiUrl}/api/upload/presigned-url?filename=${encodeURIComponent(
        resized.name
      )}&type=${encodeURIComponent(resized.type)}&size=${encodeURIComponent(resized.size)}`;

      const presignRes = await fetch(presignUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!presignRes.ok) {
        throw new Error('Presign request failed: ' + presignRes.status);
      }

      const { uploadUrl, fileUrl } = await presignRes.json();

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': resized.type },
        body: resized,
      });
      if (!uploadRes.ok) {
        throw new Error('S3 upload failed: ' + uploadRes.status);
      }

      const updated = { ...form, logoUrl: fileUrl };
      setForm(updated);

      const saveRes = await fetch(`${apiUrl}/api/settings/${userId}/company`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updated),
      });
      if (!saveRes.ok) {
        throw new Error('Failed to save logo URL');
      }

      notify('Logo uploaded!');
    } catch (err) {
      console.error('Upload error:', err);
      notify('Logo upload failed: ' + err.message);
    }

    setUploading(false);
  }

  /* ----------------------------------------
     Handle <input type="file">
  ---------------------------------------- */
  function handleFileInput(e) {
    const file = e.target.files[0];
    if (file) uploadFile(file);
  }

  /* ----------------------------------------
     Handle drag & drop upload
  ---------------------------------------- */
  async function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) uploadFile(file);
  }

  if (loading) return <p>Loading...</p>;

  return (
    <div className="company-settings">
      {/* LEFT FORM */}
      <div className="company-form">
        <div className="form-row">
          <label>Company Name</label>
          <input
            value={form.companyName}
            onChange={(e) => updateField('companyName', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Address</label>
          <AddressAutocomplete
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
            onSelect={({ street, city, state, zip }) => {
              const parts = [street, city, [state, zip].filter(Boolean).join(' ')].filter(Boolean);
              updateField('address', parts.join(', '));
            }}
            placeholder="Company Address"
          />
        </div>

        <div className="form-row">
          <label>Contact Number</label>
          <input
            value={form.contactNumber}
            onChange={(e) => updateField('contactNumber', e.target.value)}
          />
        </div>

        <div className="form-row">
          <label>Email</label>
          <input value={form.email} onChange={(e) => updateField('email', e.target.value)} />
        </div>

        <div className="form-row">
          <label>Website</label>
          <input value={form.website} onChange={(e) => updateField('website', e.target.value)} />
        </div>

        <div className="form-row">
          <label>EIN</label>
          <input value={form.ein} onChange={(e) => updateField('ein', e.target.value)} />
        </div>

        <button className="save-btn" onClick={saveCompany} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      {/* RIGHT — LOGO UPLOADER */}
      <div
        className={`company-logo-box ${isDragging ? 'drag-active' : ''}`}
        onClick={() => fileInputRef.current.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setIsDragging(false);
        }}
        onDrop={handleDrop}
      >
        <div className="logo-inner">
          {uploading ? (
            <span className="upload-status">Uploading...</span>
          ) : form.logoUrl ? (
            <img src={form.logoUrl} alt="Logo" className="company-logo-preview" />
          ) : (
            <span className="upload-placeholder">Click or drag logo here</span>
          )}

          <div className="logo-hover-overlay">
            <span>Click or drag to upload</span>
          </div>
        </div>

        <input
          type="file"
          accept=".jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </div>
    </div>
  );
}
